import React, { CSSProperties } from 'react'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { SpinnerInlineStyled } from '@mobi/component-library/Common/Spinner/Spinner.styles'
import { Icon, IconName } from '@mobi/component-library/Common/V2/Icon/Icon'
import { colors, spacing } from '@mobi/component-library/Theme/Common'

export const SettingItem: React.FC<{
  label: string
  description?: string
  isLoading?: boolean
  rightComponent?: React.ReactNode
  bottomComponent?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  style?: CSSProperties
}> = ({
  label,
  description = '',
  isLoading = false,
  rightComponent,
  bottomComponent,
  onClick,
  style,
}) => {
  const settingNameKebabCase = label.toLowerCase().replace(/ /g, '-')

  return (
    <WrapperStyled
      onClick={onClick}
      data-testid={`setting-item-${settingNameKebabCase}`}
      style={style}
    >
      <div>
        <h3>
          {label} {isLoading && <SpinnerInlineStyled color='dark' />}
        </h3>
        {description}

        <div>{bottomComponent}</div>
      </div>

      <div>{rightComponent}</div>
    </WrapperStyled>
  )
}

export const SettingsItemWithIcon: React.FC<{
  label: string
  description?: string
  iconName: IconName
  onClick?: () => void
}> = ({ label, description, iconName, onClick }) => {
  return (
    <SettingsItemWithIconStyled onClick={onClick}>
      <Icon style={{ marginTop: description ? '-1rem' : '-0.5rem' }} name={iconName}></Icon>
      <SettingItem
        label={label}
        description={description}
        style={{ flexGrow: '2', padding: `${description ? '0.75rem' : '1.5rem'} 0` }}
      />
      <Icon
        data-testid='icon-SolidChevronRight'
        name='SolidChevronRight'
        size='2.4rem'
        color={colors.neutral[900]}
        style={{ marginTop: description ? '-1rem' : '-0.5rem' }}
      />
    </SettingsItemWithIconStyled>
  )
}

// ======
// Styles
// ======

const SettingsItemWithIconStyled = styled.div({
  display: 'flex',
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
  gap: spacing.sm,
  alignItems: 'center',
  cursor: 'pointer',
})

const WrapperStyled = styled.div({
  display: 'flex',
  borderBottom: '0.1rem solid ' + hexColors.veryLightGrey,
  padding: '1.5rem 0',
  margin: '0 1rem',
  fontSize: '1.2rem',
  color: hexColors.dimGrey,

  '&:last-of-type': { borderBottom: 0 },

  '> div:first-of-type': {
    flex: 1,

    '> h3': {
      display: 'flex',
      alignItems: 'center',
      minHeight: '2.4rem',
      padding: 0,
      margin: 0,
      fontSize: '1.4rem',
      fontWeight: 'bold',
      lineHeight: 1.2,
      textAlign: 'left',
      textTransform: 'capitalize',
      background: 'none',
      color: hexColors.nero,

      span: { marginLeft: '0.5rem' },
    },

    // Bottom Component
    '> div': {
      marginTop: '0.5rem',
    },
  },

  // Right Component
  '> div:last-of-type': {
    paddingLeft: '0.5rem',

    ':empty': { display: 'none' },
  },
})
